import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  StatusPill,
  APPLICATION_STATUS_TONES,
  APPLICATION_STATUS_LABELS,
} from "@/components/StatusPill";
import { Empty } from "@/components/Empty";
import { Notice } from "@/components/Notice";
import { ApiError } from "@/lib/apiClient";
import { applications, type ApplicationDetail } from "@/features/applications/api/applications.api";
import { type UserRef } from "@/features/merchants/api/merchants.api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: ApplicationDetail }
  | { kind: "not-found" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!id) {
      setState({ kind: "error", message: "Missing application id in URL." });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    applications.get(id)
      .then((data) => { if (!cancelled) setState({ kind: "ok", data }); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setState({ kind: "not-found" });
        else if (e instanceof ApiError && e.status === 403) setState({ kind: "forbidden" });
        else setState({ kind: "error", message: e instanceof Error ? e.message : "Failed to load application." });
      });
    return () => { cancelled = true; };
  }, [id]);

  return (
    <>
      <div className="mb-4">
        <Link to="/applications" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back to applications
        </Link>
      </div>

      {state.kind === "loading" && (
        <p className="text-slate-500 text-sm">Loading application…</p>
      )}
      {state.kind === "not-found" && (
        <Notice tone="info">
          Application not found, or you don't have permission to view it.
          The API returns 404 for both cases so we don't leak which
          applications exist outside your scope.
        </Notice>
      )}
      {state.kind === "forbidden" && (
        <Notice tone="warn">
          You don't have permission to view applications (code 30.03).
        </Notice>
      )}
      {state.kind === "error" && <Notice tone="error">{state.message}</Notice>}
      {state.kind === "ok" && <Body a={state.data} />}
    </>
  );
}

function Body({ a }: { a: ApplicationDetail }) {
  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{a.dbaName}</h1>
          {a.legalName && a.legalName !== a.dbaName && (
            <p className="text-sm text-slate-500 mt-0.5">Legal: {a.legalName}</p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            Submitted {fmtDateTime(a.submittedAt)}
            {a.source && <span className="ml-3">via {a.source}</span>}
            {a.externalId && (
              <span className="ml-3 font-mono">ext {a.externalId}</span>
            )}
          </p>
        </div>
        <StatusPill
          value={a.status}
          map={APPLICATION_STATUS_TONES}
          labels={APPLICATION_STATUS_LABELS}
        />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Owner">
          {a.owner ? <UserLine user={a.owner} /> : <Empty>Unassigned</Empty>}
        </Panel>
        <Panel title="Associate">
          {a.associate ? <UserLine user={a.associate} /> : <Empty>None</Empty>}
        </Panel>
        <Panel title="Wizard progress">
          <Field label="Step" value={`${a.wizardStep} (${WIZARD_STEPS[a.wizardStep] ?? "unknown"})`} />
          {a.userNumber != null && (
            <Field label="Shared via user #" value={`${a.userNumber}`} />
          )}
        </Panel>

        <Panel title="Business">
          <Field label="DBA" value={a.dbaName} />
          <Field label="Legal" value={a.legalName} />
          <Field label="MCC" value={a.mcc} />
          <Field label="Ownership" value={a.ownershipType} />
          <Field label="Website" value={a.website} />
          <Field label="Started" value={a.businessStartDate} />
        </Panel>

        <Panel title="Contact">
          <Field label="Name" value={a.contactName} />
          <Field label="Email" value={a.email} />
          <Field label="Phone" value={a.phone} />
        </Panel>

        <Panel title="Business address">
          <Address
            line1={a.addressLine1} line2={a.addressLine2}
            city={a.city} state={a.state} zip={a.zip}
          />
        </Panel>

        {!a.dbaSameAsLegal &&
          (a.dbaAddressLine1 || a.dbaCity || a.dbaState || a.dbaZip) && (
          <Panel title="DBA location">
            <Address
              line1={a.dbaAddressLine1}
              city={a.dbaCity} state={a.dbaState} zip={a.dbaZip}
            />
          </Panel>
        )}

        <Panel title="Processing profile" wide>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6">
            <Field label="Avg monthly" value={fmtMoney(a.averageMonthlyVolume)} />
            <Field label="Avg ticket" value={fmtMoney(a.averageTransactionAmount)} />
            <Field label="Max ticket" value={fmtMoney(a.maxTransactionAmount)} />
          </div>
          <Field label="Products" value={a.productDescription} />
        </Panel>

        <Panel title="Principal owner" wide>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <Field label="Name" value={joinName(a.ownerFirstName, a.ownerLastName)} />
            <Field label="Title" value={a.ownerTitle} />
            <Field label="Date of birth" value={a.ownerDateOfBirth} />
            <Field label="Phone" value={a.ownerPhone} />
            <Field label="SSN" value={maskTail(a.ownerSsn, 4)} />
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100">
            <span className="text-xs uppercase tracking-wide text-slate-500">Home address</span>
            <Address
              line1={a.ownerHomeAddressLine1}
              city={a.ownerHomeCity} state={a.ownerHomeState} zip={a.ownerHomeZip}
            />
          </div>
        </Panel>

        <Panel title="KYC">
          <Field label="Tax ID" value={maskTail(a.taxId, 4)} />
          <Field label="EIN" value={maskTail(a.ein, 4)} />
          <Field label="Business SSN" value={maskTail(a.ssn, 4)} />
        </Panel>

        <Panel title="Banking">
          <Field label="Bank" value={a.bankName} />
          <Field label="Account type" value={a.depositAccountType} />
          <Field label="Account #" value={maskTail(a.bankAccountNumber, 4)} />
          <Field label="Routing #" value={maskTail(a.bankRoutingNumber, 4)} />
        </Panel>

        <Panel title="Review">
          <Field label="Reviewed at" value={fmtDateTime(a.reviewedAt)} />
          <Field label="Reviewed by" value={a.reviewedBy} />
        </Panel>

        {a.notes && (
          <Panel title="Notes" wide>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.notes}</p>
          </Panel>
        )}
      </div>
    </>
  );
}

// --- Helpers ----------------------------------------------------------------

const WIZARD_STEPS: Record<number, string> = {
  0: "Campaign",
  1: "Terminal Deployment",
  2: "Business",
  3: "Principal",
  4: "Processing",
  5: "Documents",
  6: "Fees",
};

function Panel({ title, wide, children }: { title: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md shadow-sm ${wide ? "lg:col-span-3" : ""}`}>
      <div className="px-4 py-2 border-b border-slate-200 text-xs font-medium uppercase tracking-wide text-slate-600">
        {title}
      </div>
      <div className="p-4 text-sm">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline gap-2 py-0.5">
      <span className="text-xs uppercase tracking-wide text-slate-500 w-28 shrink-0">{label}</span>
      <span className="text-slate-800">{value && value.length > 0 ? value : <Empty>—</Empty>}</span>
    </div>
  );
}

function UserLine({ user }: { user: UserRef }) {
  return (
    <div>
      <div className="text-slate-800 font-medium">
        {user.name}
        {user.userNumber != null && (
          <span className="text-slate-400 font-normal"> #{user.userNumber}</span>
        )}
      </div>
      <div className="text-xs text-slate-500">{user.email}</div>
    </div>
  );
}

function Address({
  line1, line2, city, state, zip,
}: {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}) {
  const lines = [
    line1,
    line2,
    [city, state, zip].filter(Boolean).join(", "),
  ].filter((l): l is string => !!l && l.length > 0);
  if (lines.length === 0) return <Empty>No address on file.</Empty>;
  return (
    <div className="text-slate-800 leading-relaxed">
      {lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

function joinName(first: string | null, last: string | null): string | null {
  const n = [first, last].filter(Boolean).join(" ");
  return n.length > 0 ? n : null;
}

function fmtMoney(n: number | null): string | null {
  if (n == null) return null;
  return "$" + n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function maskTail(value: string | null | undefined, keep: number): string | null {
  if (!value) return null;
  if (value.length <= keep) return "•".repeat(value.length);
  return "•".repeat(value.length - keep) + value.slice(-keep);
}
